import { Injectable } from '@angular/core';
import { PrestationModel } from 'src/app/core/models/prestation.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseService } from 'src/app/core/base/base.service';
import { map, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { ObjectApiInterface } from '../../../../core/base/interfaces/object-api.interface';
import { ProductModel } from '../../../../core/models/product.model';
import { QuoteModel } from '../../../../core/models/quote-model';
import { AuthService } from '../../../../core/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class PrestationService extends BaseService<PrestationModel> {
  defaultSort = 'createdAt';

  constructor(
    public http: HttpClient,
    public authService: AuthService,
  ) {
    super(http, 'prestation', authService);
  }

  // BehaviorSubject pour stocker les prestations
  protected _prestation = new BehaviorSubject<PrestationModel[]>([]);
  readonly prestation$ = this._prestation.asObservable();

  // BehaviorSubject pour les métadonnées (compteur, état du cache)
  private _prestationsMetadata = new BehaviorSubject<{
    totalData: number;
    loaded: boolean;
  }>({ totalData: 0, loaded: false });
  readonly prestationsMetadata$ = this._prestationsMetadata.asObservable();

  findAll(
    page?: number,
    perPage?: number,
    sortField: string = this.defaultSort,
    sortOrder: string = 'desc',
    search?: string,
    filters?: any,
  ): Observable<ObjectApiInterface<any[]>> {
    // // TODO: add priceTTC in product model with aggregation
    if (sortField === 'priceTTC') {
      sortField = 'priceHT';
    }
    return super.findAll(page, perPage, sortField, sortOrder, search, filters);
  }

  // getOrderProductsPrestations(page?: number, perPage?: number, sortField: string = this.defaultSort, sortOrder: string = 'desc', search?: string, filters?: PrestationModel | any): Observable<ObjectApiInterface<any[]>> {
  //     if (sortField === 'priceTTC') {
  //         sortField = 'priceHT';
  //     }
  //     return super.getOrderProductsPrestations(page, perPage, sortField, sortOrder, search, filters);
  // }

  findFiltered(
    page?: number,
    perPage?: number,
    sortField: string = this.defaultSort,
    sortOrder: string = 'desc',
    search?: string,
    filters?: any,
  ): Observable<ObjectApiInterface<any[]>> {
    return this.http.get<ObjectApiInterface<PrestationModel[]>>(`${this.baseUrlApi}/list`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? '30')
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  getsupplierRelation(_id): Observable<ObjectApiInterface<ProductModel[]>> {
    return this.http.get<ObjectApiInterface<ProductModel[]>>(`${this.baseUrlApi}/getsupplierRelation/${_id}`);
  }

  addGenerics(data: any) {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/addGenerics`, data).pipe(
      map(x => {
        // this._entity.next(x.data);
        return x;
      }),
    );
  }

  getPrestationsInTechnicalPerimeters(contractIds: string[], itemIds: string[]) {
    return this.http
      .post<ObjectApiInterface<any>>(`${this.baseUrlApi}/getPrestationsInTechnicalPerimeters`, { contractIds, itemIds })
      .pipe(
        map(x => {
          // this._entity.next(x.data);
          return x;
        }),
      );
  }

  importPrestationContractItems(entity: PrestationModel, contractId: string, quoteId?: string): Observable<any> {
    let params = new HttpParams();
    if (quoteId) {
      params = params.set('quoteId', quoteId);
    }
    return this.http.post<ObjectApiInterface<PrestationModel>>(
      `${this.baseUrlApi}/importPrestationContract/${contractId}`,
      entity,
      { params },
    );
  }

  importPrestationContractModificationItems(entity: PrestationModel, contractId: string): Observable<any> {
    return this.http.post<ObjectApiInterface<PrestationModel>>(
      `${this.baseUrlApi}/importPrestationContractModificationItems/${contractId}`,
      entity,
    );
  }

  downloadExcelTemplatePrestationContract(contractId: string): Observable<ObjectApiInterface<PrestationModel>> {
    return this.http.get<ObjectApiInterface<PrestationModel>>(
      `${this.baseUrlApi}/downloadPrestationContractTemplate/${contractId}`,
    );
  }

  downloadExcelTemplatePrestationContractModificationItem(
    contractId: string,
  ): Observable<ObjectApiInterface<PrestationModel>> {
    return this.http.get<ObjectApiInterface<PrestationModel>>(
      `${this.baseUrlApi}/downloadExcelTemplatePrestationContractModificationItem/${contractId}`,
    );
  }

  validateGenericAdministration(data: any[]) {
    return this.http.patch<ObjectApiInterface<any>>(`${this.baseUrlApi}/validateGenericAdministration`, data).pipe(
      map(x => {
        // this._entity.next(x.data);
        return x;
      }),
    );
  }

  suspend(entity: PrestationModel): Observable<ObjectApiInterface<PrestationModel>> {
    return this.http.get<ObjectApiInterface<PrestationModel>>(`${this.baseUrlApi}/suspend/${entity._id}`);
  }

  unsuspend(entity: PrestationModel): Observable<ObjectApiInterface<PrestationModel>> {
    return this.http.get<ObjectApiInterface<PrestationModel>>(`${this.baseUrlApi}/unsuspend/${entity._id}`);
  }

  downloadExcelTemplateProductContractModificationItem(
    contractId: string,
  ): Observable<ObjectApiInterface<ProductModel>> {
    return this.http.get<ObjectApiInterface<ProductModel>>(
      `${this.baseUrlApi}/downloadExcelTemplateProductContractModificationItem/${contractId}`,
    );
  }

  /**
   * Charge les prestations ET les stocke dans le BehaviorSubject (cache)
   */
  loadPrestations(
    page?: number,
    perPage?: number,
    sortField?: string,
    sortOrder?: string,
    search?: string,
    filters?: any,
  ): Observable<ObjectApiInterface<PrestationModel[]>> {
    return this.findAll(page, perPage, sortField, sortOrder, search, filters).pipe(
      tap(res => {
        // Stocker dans le BehaviorSubject
        this._prestation.next(res.data);
        this._prestationsMetadata.next({
          totalData: res.totalData,
          loaded: true,
        });
      }),
    );
  }

  /**
   * Vide le cache des prestations
   */
  clearCache(): void {
    this._prestation.next([]);
    this._prestationsMetadata.next({ totalData: 0, loaded: false });
  }
}
